import React, { useCallback, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import {
  AvatarBadge,
  Dropdown,
  DropdownContent,
  DropdownGroup,
  DropdownItem,
  DropdownSub,
  DropdownSubTrigger,
  DropdownTrigger,
  EmployeeIdBadge as EmployeeIdBadgeIcon,
  MySettings as MySettingsIcon,
  CompanySettings as CompanySettingsIcon,
  NewPassword as NewPasswordIcon,
  Help2 as Help2Icon,
  Logout as LogoutIcon,
  Icon,
  toast,
  ListItemComp,
  ListItemCompLeading,
  ListItemCompContent,
  ListItemCompTitle,
} from '@mesh.ai/rabbit';
import { KNOWLEDGE_HUB_URI } from 'Deer/constants/projectConstants';
import { staticRoutes } from 'Deer/constants/routes';
import { getProfileRole, getUserAccess } from 'Deer/helpers/getUserAccess';
import { useHoverDropdownMenu } from 'Deer/hooks/useHoverDropdownMenu';
import { logoutLoggedInEmployee } from 'actions/loggedInEmployee';
import NotificationsPanel from 'components/Notifications/NotificationsPanel';
import { useNotifications } from 'components/Notifications/context';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { checkSettingsPageRevampEnabled } from 'utils/helpers/featureFlags';

import { Logout, Settings, Help } from 'assets/assetsV1/iconComponents';
import { Password } from 'assets/assetsV1/iconComponents/Password';
import { Person2 } from 'assets/assetsV1/iconComponents/Person2';

import {
  sendClickedOnProfileIcon,
  sendClickedViewProfileEvent,
  sendLogoutEvent,
  sendProfilePageViewEvent,
} from 'constants/mixpanelHelper';

import { resetAuth0Password } from 'services/auth';
import { getPrimaryPod } from 'services/helpers/team';

import { NotificationBadge } from 'uiToolkitV1/NotificationBadge';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const ProfileMenu = () => {
  const {
    user: { id: employeeId, fullName, teams, profilePhotoSrc },
  } = useAuthenticatedUser() ?? {};
  const dispatch = useDispatch();
  const [profileMenuStatus, setProfileStatus] = useHoverDropdownMenu();

  const [isNotificationPopupOpen, setNotificationPopupOpen] = useState(false);

  const { isPasswordEnabled } = getUserAccess();
  const role = getProfileRole();

  const resetPasswordHandler = async () => {
    try {
      const res = await resetAuth0Password();
      toast.success(res.message || 'Password reset link is sent to your email');
    } catch (error) {
      toast.error(error.message || 'Some error occurred! Please try again.');
    }
  };

  const logout = useCallback(() => {
    // @ts-expect-error: expected parameters are different
    dispatch(logoutLoggedInEmployee());
  }, [dispatch]);

  const {
    state: { notificationsCount },
  } = useNotifications();

  const profileRoute = useMemo(() => staticRoutes.profile.replace(':employeeId', employeeId), [employeeId]);

  const isSettingsPageRevampEnabled = checkSettingsPageRevampEnabled();

  if (isSettingsPageRevampEnabled) {
    const { isPlatformAdmin } = getUserAccess();

    return (
      <Dropdown
        open={profileMenuStatus.isOpen || profileMenuStatus.isMenuHovered || profileMenuStatus.isTriggerHovered}
        onOpenChange={(isOpen) => {
          sendClickedOnProfileIcon();
          setProfileStatus((curStatus) => ({ ...curStatus, isOpen }));

          if (isOpen === false) {
            setNotificationPopupOpen(isOpen);
          }
        }}
        modal={false}
      >
        <DropdownTrigger
          onClick={() => {
            setProfileStatus((curStatus) => ({ ...curStatus, isTriggerHovered: true }));
          }}
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') {
              sendClickedOnProfileIcon();
              setProfileStatus((curStatus) => ({ ...curStatus, isTriggerHovered: true }));
            }
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse')
              setProfileStatus((curStatus) => ({ ...curStatus, isTriggerHovered: false }));
          }}
        >
          <Box
            _hover={{ bg: 'rgb(202, 232, 240)', borderRadius: '8px' }}
            _active={{
              bg: 'rgb(202, 232, 240)',
              borderRadius: '8px',
            }}
            bg={profileMenuStatus.isOpen ? 'rgb(202, 232, 240)' : 'transparent'}
            borderRadius={profileMenuStatus.isOpen ? '8px' : '0'}
            width="56px"
            height="56px"
            padding="8px"
            id="primary-nav-add-btn"
            data-testid="primary-nav-profile-btn"
          >
            <NotificationBadge
              notificationCount={notificationsCount ?? 0}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transform={-0.5}
            >
              <AvatarBadge
                showUpdateButton={false}
                userName={fullName}
                badgeLabel={role === 'Admin' ? role : undefined}
                size="xl"
                src={profilePhotoSrc}
              />
            </NotificationBadge>
          </Box>
        </DropdownTrigger>
        <DropdownContent
          side="right"
          collisionPadding={{
            bottom: 8,
          }}
          style={{
            width: '220px',
            padding: '8px',
          }}
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') setProfileStatus((curStatus) => ({ ...curStatus, isMenuHovered: true }));
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse')
              setProfileStatus((curStatus) => ({ ...curStatus, isMenuHovered: false }));
          }}
        >
          <DropdownGroup>
            <Link to={profileRoute}>
              <DropdownItem
                onClick={() => {
                  sendClickedViewProfileEvent({
                    eventProperties: {
                      empId: employeeId,
                      source: 'My Profile',
                      teamId: getPrimaryPod(teams)?.id ?? null,
                      profileType: 'User',
                    },
                  });
                  sendProfilePageViewEvent({ eventProperties: { source: 'My Profile' } });
                }}
              >
                <ListItemComp style={{ padding: 0 }}>
                  <ListItemCompLeading style={{ color: '#666666' }}>
                    <Icon IconSVG={EmployeeIdBadgeIcon} />
                  </ListItemCompLeading>
                  <ListItemCompContent>
                    <ListItemCompTitle bold={false}>Profile</ListItemCompTitle>
                  </ListItemCompContent>
                </ListItemComp>
              </DropdownItem>
            </Link>

            <DropdownSub>
              <DropdownSubTrigger
                onPointerEnter={(event) => {
                  if (event.pointerType === 'mouse') setNotificationPopupOpen(true);
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType === 'mouse') setNotificationPopupOpen(false);
                }}
              >
                <NotificationsPanel
                  closeMenu={() => {}}
                  isHovered={isNotificationPopupOpen}
                  onNotificationClick={() => setProfileStatus((curStatus) => ({ ...curStatus, isOpen: false }))}
                />
              </DropdownSubTrigger>
            </DropdownSub>
            <Link to={staticRoutes['my-data']['my-settings'].notifications}>
              <DropdownItem>
                <ListItemComp style={{ padding: 0 }}>
                  <ListItemCompLeading style={{ color: '#666666' }}>
                    <Icon IconSVG={MySettingsIcon} />
                  </ListItemCompLeading>
                  <ListItemCompContent>
                    <ListItemCompTitle bold={false}>My Settings</ListItemCompTitle>
                  </ListItemCompContent>
                </ListItemComp>
              </DropdownItem>
            </Link>

            {isPlatformAdmin && (
              <Link to={staticRoutes.company.settings.platform.notifications}>
                <DropdownItem>
                  <ListItemComp style={{ padding: 0 }}>
                    <ListItemCompLeading style={{ color: '#666666' }}>
                      <Icon IconSVG={CompanySettingsIcon} />
                    </ListItemCompLeading>
                    <ListItemCompContent>
                      <ListItemCompTitle bold={false}>Company Settings</ListItemCompTitle>
                    </ListItemCompContent>
                  </ListItemComp>
                </DropdownItem>
              </Link>
            )}

            {isPasswordEnabled ? (
              <DropdownItem onClick={resetPasswordHandler}>
                <ListItemComp style={{ padding: 0 }}>
                  <ListItemCompLeading style={{ color: '#666666' }}>
                    <Icon IconSVG={NewPasswordIcon} />
                  </ListItemCompLeading>
                  <ListItemCompContent>
                    <ListItemCompTitle bold={false}>Password Reset</ListItemCompTitle>
                  </ListItemCompContent>
                </ListItemComp>
              </DropdownItem>
            ) : null}

            <a target="_blank" rel="noopener noreferrer" href={KNOWLEDGE_HUB_URI}>
              <DropdownItem>
                <ListItemComp style={{ padding: 0 }}>
                  <ListItemCompLeading style={{ color: '#666666' }}>
                    <Icon IconSVG={Help2Icon} />
                  </ListItemCompLeading>
                  <ListItemCompContent>
                    <ListItemCompTitle bold={false}>Help Center</ListItemCompTitle>
                  </ListItemCompContent>
                </ListItemComp>
              </DropdownItem>
            </a>

            <DropdownItem
              onClick={() => {
                sendLogoutEvent();
                logout();
              }}
            >
              <ListItemComp style={{ padding: 0 }}>
                <ListItemCompLeading style={{ color: '#666666' }}>
                  <Icon IconSVG={LogoutIcon} />
                </ListItemCompLeading>
                <ListItemCompContent>
                  <ListItemCompTitle bold={false}>Logout</ListItemCompTitle>
                </ListItemCompContent>
              </ListItemComp>
            </DropdownItem>
          </DropdownGroup>
        </DropdownContent>
      </Dropdown>
    );
  }

  return (
    <Dropdown
      open={profileMenuStatus.isOpen || profileMenuStatus.isMenuHovered || profileMenuStatus.isTriggerHovered}
      onOpenChange={(isOpen) => {
        sendClickedOnProfileIcon();
        setProfileStatus((curStatus) => ({ ...curStatus, isOpen }));

        if (isOpen === false) {
          setNotificationPopupOpen(isOpen);
        }
      }}
      modal={false}
    >
      <DropdownTrigger
        onClick={() => {
          setProfileStatus((curStatus) => ({ ...curStatus, isTriggerHovered: true }));
        }}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') {
            sendClickedOnProfileIcon();
            setProfileStatus((curStatus) => ({ ...curStatus, isTriggerHovered: true }));
          }
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse')
            setProfileStatus((curStatus) => ({ ...curStatus, isTriggerHovered: false }));
        }}
      >
        <Box
          _hover={{ bg: 'rgb(202, 232, 240)', borderRadius: '8px' }}
          _active={{
            bg: 'rgb(202, 232, 240)',
            borderRadius: '8px',
          }}
          bg={profileMenuStatus.isOpen ? 'rgb(202, 232, 240)' : 'transparent'}
          borderRadius={profileMenuStatus.isOpen ? '8px' : '0'}
          width="56px"
          height="56px"
          padding="8px"
          id="primary-nav-add-btn"
          data-testid="primary-nav-profile-btn"
        >
          <NotificationBadge
            notificationCount={notificationsCount ?? 0}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transform={-0.5}
          >
            <AvatarBadge
              showUpdateButton={false}
              userName={fullName}
              badgeLabel={role === 'Admin' ? role : undefined}
              size="xl"
              src={profilePhotoSrc}
            />
          </NotificationBadge>
        </Box>
      </DropdownTrigger>
      <DropdownContent
        side="right"
        collisionPadding={{
          bottom: 8,
        }}
        style={{
          width: '220px',
        }}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') setProfileStatus((curStatus) => ({ ...curStatus, isMenuHovered: true }));
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') setProfileStatus((curStatus) => ({ ...curStatus, isMenuHovered: false }));
        }}
      >
        <DropdownGroup>
          <Link to={profileRoute}>
            <DropdownItem
              onClick={() => {
                sendClickedViewProfileEvent({
                  eventProperties: {
                    empId: employeeId,
                    source: 'My Profile',
                    teamId: getPrimaryPod(teams)?.id ?? null,
                    profileType: 'User',
                  },
                });
                sendProfilePageViewEvent({ eventProperties: { source: 'My Profile' } });
              }}
            >
              <Person2 boxSize="24px" marginRight="0.5rem" />
              <TextBodyMedium padding="2px">Profile</TextBodyMedium>
            </DropdownItem>
          </Link>

          <DropdownSub>
            <DropdownSubTrigger
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') setNotificationPopupOpen(true);
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === 'mouse') setNotificationPopupOpen(false);
              }}
            >
              <NotificationsPanel
                closeMenu={() => {}}
                isHovered={isNotificationPopupOpen}
                onNotificationClick={() => setProfileStatus((curStatus) => ({ ...curStatus, isOpen: false }))}
              />
            </DropdownSubTrigger>
          </DropdownSub>
          <Link to={staticRoutes['my-data']['my-settings'].notifications}>
            <DropdownItem>
              <Settings boxSize="24px" marginRight="0.5rem" />
              <TextBodyMedium padding="2px">Settings</TextBodyMedium>
            </DropdownItem>
          </Link>

          {isPasswordEnabled ? (
            <DropdownItem onClick={resetPasswordHandler}>
              <Password boxSize="24px" marginRight="0.5rem" />
              <TextBodyMedium padding="2px">Password Reset</TextBodyMedium>
            </DropdownItem>
          ) : null}

          <a target="_blank" rel="noopener noreferrer" href={KNOWLEDGE_HUB_URI}>
            <DropdownItem>
              <Help boxSize="24px" marginRight="0.5rem" />
              <TextBodyMedium padding="2px">Help Center</TextBodyMedium>
            </DropdownItem>
          </a>

          <DropdownItem
            onClick={() => {
              sendLogoutEvent();
              logout();
            }}
          >
            <Logout boxSize="24px" marginRight="0.5rem" />
            <TextBodyMedium padding="2px">Logout</TextBodyMedium>
          </DropdownItem>
        </DropdownGroup>
      </DropdownContent>
    </Dropdown>
  );
};

export { ProfileMenu };
